import { useEffect, useState } from "react";
import CloseIcon from "@assets/svg/CloseIcon";
import Modal from "../../Modal/Modal";
import { Controller, useForm, useWatch } from "react-hook-form";
import dayjs from "@lib/dayjs";
import CustomTimePicker from "../../CustomTimePicker/CustomTimePicker";
import CustomCheckbox from "../../CustomCheckbox/CustomCheckbox";

const availabilityTypes = [
  {
    label: "I'm available at",
    value: 1,
  },
  {
    label: "I'm unavailable at",
    value: 2,
  },
];

const durations = [
  {
    label: "1 hours",
    value: "1",
  },
  {
    label: "2 hours",
    value: "2",
  },
  {
    label: "3 hours",
    value: "3",
  },
];

const recurrencyTypes = [
  {
    label: "Daily",
    value: "daily",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
];

const days = [
  {
    label: "Sunday",
    value: "sun",
  },
  {
    label: "Monday",
    value: "mon",
  },
  {
    label: "Tuesday",
    value: "tue",
  },
  {
    label: "Wednesday",
    value: "wed",
  },
  {
    label: "Thursday",
    value: "thu",
  },
  {
    label: "Friday",
    value: "fri",
  },
  {
    label: "Saturday",
    value: "sat",
  },
];

const AddAvailabilityModal = ({ showModal, setShowModal }) => {
  const formMethods = useForm({
    defaultValues: {
      date: dayjs().toDate(),
      startTime: dayjs(),
      endTime: dayjs().add(1, "hours"),
      duration: "1",
    },
  });
  const fields = useWatch({ control: formMethods?.control });
  const [selectedAvailabilityType, setSelectedAvailabilityType] = useState(1);
  const [doesRepeat, setDoesRepeat] = useState(false);
  const [selectedRecurrenceType, setSelectedRecurrenceType] = useState();
  const [selectedDays, setSelectedDays] = useState([]);

  const handleDayClick = (value) => {
    setSelectedDays((prev) => {
      const isAlreadyExist = prev?.find((item) => item === value);
      if (isAlreadyExist) {
        return prev?.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  useEffect(() => {
    if (!showModal) {
      formMethods?.reset();
      setDoesRepeat(false);
      setSelectedAvailabilityType(1);
      setSelectedRecurrenceType();
      setSelectedDays([]);
    }
  }, [showModal]);

  return (
    <Modal isOpen={showModal} containerStyle="max-w-3xl">
      <div>
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-3">
          <span className="font-semibold text-charcoal">Availability Form</span>
          <button onClick={() => setShowModal(false)}>
            <CloseIcon className={"w-6 h-6"} fill="red" />
          </button>
        </div>
        {/* Form container */}
        <div className="flex flex-col pt-3">
          {/* Availability Types */}
          <div className="flex items-center gap-10">
            {availabilityTypes?.map((availabilityType) => {
              const isSelected =
                availabilityType?.value === selectedAvailabilityType;
              return (
                <button
                  key={availabilityType?.value}
                  onClick={() =>
                    setSelectedAvailabilityType(availabilityType?.value)
                  }
                  className="flex items-center gap-2"
                >
                  <div
                    className={`h-4 w-4 rounded-full border-2 ${
                      isSelected ? "border-gray-400" : "border-gray-200"
                    } flex`}
                  >
                    {isSelected ? (
                      <div className="w-2 h-2 bg-primary rounded-full m-auto" />
                    ) : null}
                  </div>
                  <span className="font-medium text-xs">
                    {availabilityType?.label}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Time Picker */}
          <div className="flex flex-col gap-1 py-4 mt-4 border-y">
            <span className="font-medium text-sm">Time Picker</span>
            <div className="flex items-center relative gap-5">
              <Controller
                name="startTime"
                control={formMethods?.control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <CustomTimePicker
                      value={value}
                      containerStyle={"w-20"}
                      placeholder={"Start Time"}
                      onChange={(e) => {
                        const hr = e?.split(":")[0];
                        const min = e?.split(":")[1];
                        const startTime = dayjs(fields?.date)
                          .set("hours", hr)
                          .set("minutes", min);
                        const endTime = dayjs(startTime).add(
                          parseInt(fields?.duration),
                          "hours"
                        );
                        onChange(startTime);
                        formMethods.setValue("endTime", endTime);
                        // formMethods?.trigger("specificDateTime");
                      }}
                    />
                  </>
                )}
              />
              <Controller
                name="endTime"
                control={formMethods?.control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <CustomTimePicker
                      disabled
                      value={value}
                      containerStyle={"w-20"}
                      placeholder={"End Time"}
                    />
                  </>
                )}
              />
              <div className="flex gap-2 items-center">
                <span className="font-medium text-xs">Duration:</span>
                <Controller
                  name="duration"
                  control={formMethods?.control}
                  render={({ field }) => (
                    <select
                      {...field}
                      onChange={(e) => {
                        field?.onChange(e?.target?.value);
                        const endTime = dayjs(fields?.startTime).add(
                          parseInt(e?.target?.value),
                          "hours"
                        );
                        formMethods?.setValue("endTime", endTime);
                      }}
                      className="border rounded-md text-xs py-2 px-2 font-medium"
                    >
                      {durations?.map((duration) => {
                        return (
                          <option key={duration?.value} value={duration?.value}>
                            {duration?.label}
                          </option>
                        );
                      })}
                    </select>
                  )}
                />
              </div>
            </div>
            {/* Does repeat */}
            <div className="flex items-center gap-2 mt-3">
              <CustomCheckbox
                defaultValue={doesRepeat}
                onChange={(value) => setDoesRepeat(value)}
              />
              <span className="font-medium text-sm">Does Repeat</span>
            </div>
          </div>
          {/* Recurrence pattern */}
          {doesRepeat ? (
            <div className="mt-4">
              <span className="font-medium text-sm">Recurrence Pattern</span>
              <div className="flex gap-10 mt-3">
                <div className="flex flex-col gap-3">
                  {recurrencyTypes?.map((recurrence) => {
                    const isSelected =
                      selectedRecurrenceType === recurrence?.value;
                    return (
                      <div
                        onClick={() =>
                          setSelectedRecurrenceType(recurrence?.value)
                        }
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <div className="flex items-center justify-center rounded-full h-4 w-4 border-2">
                          {isSelected ? (
                            <div className="w-2 h-2 bg-light-black rounded-full" />
                          ) : null}
                        </div>
                        <span className="font-medium text-xs text-light-black">
                          {recurrence?.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {selectedRecurrenceType === "weekly" ? (
                  <div className="flex flex-wrap gap-3 border-l pl-8">
                    {days?.map((day) => {
                      const isSelected = selectedDays?.includes(day?.value);
                      return (
                        <div
                          onClick={() => handleDayClick(day?.value)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div className="flex items-center justify-center rounded-full h-4 w-4 border-2">
                            {isSelected ? (
                              <div className="w-2 h-2 bg-light-black rounded-full" />
                            ) : null}
                          </div>
                          <span className="font-medium text-xs text-light-black">
                            {day?.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="flex items-center mt-5 justify-end gap-5">
            <button
              onClick={() => setShowModal(false)}
              className="font-semibold text-red-500 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button className="bg-primary rounded-md px-3 py-1 text-white font-semibold">
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddAvailabilityModal;
